import React, { FunctionComponent, useState } from "react";
import styles from "./DynamicFormActivities.module.scss";
import DynamicFormActivitiesProps from "./DynamicFormActivities.interface";
import BackgroundMedia from "components/BackgroundMedia/BackgroundMedia";
import combineClasses from "helpers/combine-classes";
import getWrapperClasses from "blocks/helpers/getWrapperClasses";
import setInViewClasses from "blocks/hooks/setInViewClasses";
import useSplitBackground from "blocks/hooks/useSplitBackground";
import useWrapperStyles from "blocks/hooks/useWrapperStyles";
import Select from "react-select";
import Button from "components/Button/Button";
import BlockButton from "blocks/models/block-button.interface";
import ContactForm from "../DynamicForm/components/ContactForm/ContactForm";

const DynamicFormActivities: FunctionComponent<DynamicFormActivitiesProps> = (props): JSX.Element => {
    const wrapperClasses = getWrapperClasses(props.settings);
    const wrapperStyles = useWrapperStyles(props.settings);
    const { inViewElementRef, inViewClasses } = setInViewClasses(styles);
    const { splitClasses, splitStyles } = useSplitBackground(props);

    const [selectedCity, setSelectedCity] = useState<any | null>(null);
    const [selectedActivity, setSelectedActivity] = useState<any | null>(null);
    const [contactMe, setContactMe] = useState<boolean>(false);

    const handleActivityChange = (selectedOption: any) => {
        const activity = props.content?.activitiesList?.find((item: any) => item.content.activityName === selectedOption?.value);

        setSelectedActivity(activity);
        setSelectedCity(null);
        setContactMe(false);
    };

    const handleCityChange = (selectedOption: any) => {
        const city = selectedActivity?.content?.cities?.find((item: any) => item.content.cityName === selectedOption?.value);
        setSelectedCity(city);
    };

    const cityOptions =
        selectedActivity?.content?.cities?.map((city: any) => ({
            value: city.content.cityName,
            label: city.content.cityName,
        })) ?? [];

    const handleToggleForm = () => {
        setContactMe(!contactMe);
    };

    return (
        <section
            className={combineClasses(styles.wrapper, inViewClasses, ...wrapperClasses)}
            style={{ ...wrapperStyles, ...splitStyles }}
            ref={inViewElementRef}
        >
            <div className={combineClasses(styles.container, ...splitClasses)}>
                <div className={styles.dropdown_container}>
                    <Select
                        className={styles.select}
                        classNamePrefix="select"
                        options={
                            props.content?.activitiesList?.map((item: any) => ({
                                value: item.content.activityName,
                                label: item.content.activityName,
                            })) ?? []
                        }
                        onChange={handleActivityChange}
                        aria-label=""
                        isSearchable={false}
                        placeholder="Vælg et arrerangement"
                        isClearable={false}
                    />

                    {selectedActivity && cityOptions.length > 0 && (
                        <div>
                            <Select
                                className={styles.select}
                                classNamePrefix="select"
                                options={cityOptions}
                                placeholder="Vælg en by"
                                value={
                                    selectedCity
                                        ? {
                                              value: selectedCity.content.cityName,
                                              label: selectedCity.content.cityName,
                                          }
                                        : null
                                }
                                onChange={handleCityChange}
                                isSearchable={false}
                                isClearable={false}
                            />
                        </div>
                    )}
                </div>

                {selectedCity && (
                    <div className={styles.activity_details}>
                        <div className={styles.buttons}>
                            {/* Read more btn */}
                            {selectedCity.content.readMoreCTA && selectedCity.content.readMoreCTA.length > 0 ? (
                                <div className={styles.button}>
                                    {selectedCity.content.readMoreCTA.map((button: BlockButton) => (
                                        <Button
                                            key={button.contentUdi}
                                            to={button.content.link}
                                            className={styles.button}
                                            variant={button.content.variant}
                                            {...button.content}
                                        >
                                            {button.content.label}
                                        </Button>
                                    ))}
                                </div>
                            ) : null}

                            {/* Toggle form btn */}
                            {selectedCity.content.toggleFormButton && selectedCity.content.toggleFormButton.length > 0 ? (
                                <div className={styles.button}>
                                    {selectedCity.content.toggleFormButton.map((button: BlockButton) => (
                                        <Button
                                            key={button.contentUdi}
                                            className={styles.button}
                                            variant={button.content.variant}
                                            onClick={handleToggleForm}
                                        >
                                            {button.content.label}
                                        </Button>
                                    ))}
                                </div>
                            ) : null}
                        </div>
                    </div>
                )}

                {/* Contact form */}
                {selectedCity && contactMe && selectedActivity ? (
                    <ContactForm
                        {...selectedCity}
                        selectedCity={selectedCity?.content.cityName}
                        selectedActivity={selectedActivity?.content.activityName}
                    />
                ) : null}
            </div>

            {props.settings.backgroundMedia?.[0] && props.settings.backgroundMedia.length > 0 ? (
                <BackgroundMedia
                    key={props.contentUdi}
                    {...props.settings.backgroundMedia[0]}
                />
            ) : null}
        </section>
    );
};

export default DynamicFormActivities;
